import { FormControl, Grid } from "@mui/material";
import convert, { Unit } from "convert";
import { get, set } from "lodash";
import { FC, useEffect, useRef, useState } from "react";
import { createDefaultVital } from ".";
import { BmiInputProps } from "../../../../../../components/widgets/modals/encounter";
import CodingSystems from "../../../../../../constants/CodingSystems";
import usePractitioner from "../../../../../../hooks/usePractitioner";
import { calculateBsa } from "../../../../../../utils/bsaUtils";
import {
  getSimpleVitalValue,
  getWeightKg,
  updateMetaData,
} from "../VitalsFormHelper";
import CdsFeedbackTextField from "./CdsFeedbackTextField";
import VitalCdsUnitTypography from "./VitalCdsUnitTypography";

const BSA_LOINC = "8277-6";
const BSA_DISPLAY = "Body Surface Area";
const BSA_UNIT = "m2";

const heightInCentimeters = (height: {
  valueQuantity: { value: number; unit: string };
}) => {
  return convert(
    height?.valueQuantity?.value ?? 0,
    (height?.valueQuantity?.unit as Unit) ?? "cm",
  ).to("cm");
};

const isCompleteBsaObservation = (vital: unknown) => {
  if (!vital || typeof vital !== "object") return false;
  return get(vital, "code.coding[0].code") === BSA_LOINC;
};

const BsaInput: FC<BmiInputProps> = ({
  encounter,
  patient,
  value,
  onChange,
  height,
  weight,
  disabled,
  size,
}) => {
  const practitionerReference = usePractitioner();
  const [bsa, setBsa] = useState<any>();
  const observationRef = useRef<any>();
  const previousHeightValue = useRef<number | null>(null);
  const previousWeightValue = useRef<number | null>(null);

  const createBaseObservation = () => {
    const existing = isCompleteBsaObservation(observationRef.current)
      ? observationRef.current
      : isCompleteBsaObservation(value)
        ? value
        : null;

    const base = createDefaultVital(
      existing?.encounter ?? encounter,
      existing?.subject ?? patient,
      CodingSystems.OBSERVATION_CODE,
      BSA_LOINC,
      BSA_DISPLAY,
      existing?.valueQuantity?.unit ?? BSA_UNIT,
      existing?.valueQuantity?.value,
    );

    if (existing?.id) {
      base.id = existing.id;
    }
    if (existing?.status) {
      base.status = existing.status;
    }
    if (existing?.meta) {
      base.meta = existing.meta;
    }
    if (existing?.performer) {
      base.performer = existing.performer;
    }
    if (existing?.effectiveDateTime) {
      base.effectiveDateTime = existing.effectiveDateTime;
    }

    return base;
  };

  const computeBsaValue = () => {
    if (!height?.valueQuantity?.value || !weight?.valueQuantity?.value) {
      return null;
    }

    const ht = heightInCentimeters(height);
    const wt =
      weight?.valueQuantity?.unit === "kg"
        ? weight.valueQuantity.value
        : getWeightKg(weight);

    if (!ht || !wt) {
      return null;
    }

    const result = calculateBsa(ht, wt);
    if (Number.isNaN(result) || result === Infinity) {
      return null;
    }

    return result.toFixed(2);
  };

  useEffect(() => {
    if (!isCompleteBsaObservation(value)) return;
    observationRef.current = value;
    setBsa(value);
  }, [value]);

  useEffect(() => {
    const nextHeight = getSimpleVitalValue(height);
    const nextWeight = getSimpleVitalValue(weight);
    const savedBsa =
      getSimpleVitalValue(value) ?? getSimpleVitalValue(observationRef.current);

    const heightEdited =
      previousHeightValue.current != null &&
      previousHeightValue.current !== nextHeight;
    const weightEdited =
      previousWeightValue.current != null &&
      previousWeightValue.current !== nextWeight;

    if (nextHeight != null) {
      previousHeightValue.current = nextHeight;
    }
    if (nextWeight != null) {
      previousWeightValue.current = nextWeight;
    }

    if (savedBsa != null && !heightEdited && !weightEdited) {
      if (isCompleteBsaObservation(value)) {
        observationRef.current = value;
        setBsa(value);
      }
      return;
    }

    const bodySurfaceArea = computeBsaValue();
    const newBsa = createBaseObservation();
    set(newBsa, "valueQuantity.value", bodySurfaceArea);
    set(newBsa, "valueQuantity.unit", BSA_UNIT);
    observationRef.current = newBsa;
    setBsa(newBsa);

    if (String(bodySurfaceArea ?? "") === String(savedBsa ?? "")) {
      return;
    }

    updateMetaData(newBsa, practitionerReference);
    onChange(newBsa);
  }, [height, weight, encounter, patient, value]);

  return (
    <Grid item xs={12} position="relative">
      <FormControl fullWidth>
        <CdsFeedbackTextField
          value={getSimpleVitalValue(bsa) ?? ""}
          disabled={disabled}
          onChange={() => {}}
          id="vitals-bsa"
          label="BSA"
          inputMode="numeric"
          autoComplete="off"
          InputProps={{
            endAdornment: (
              <VitalCdsUnitTypography disabled={disabled}>
                {get(bsa, "valueQuantity.unit") ?? BSA_UNIT}
              </VitalCdsUnitTypography>
            ),
          }}
          variant="outlined"
          size={size || "medium"}
          type="text"
          sx={{
            "& .MuiInputBase-input": {
              cursor: "not-allowed",
              borderRadius: "8px",
            },
          }}
        />
      </FormControl>
    </Grid>
  );
};

export default BsaInput;
